import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { AppHeader } from '@teliaee/telia-megamenu';
import SsoClient from '@teliaee/telia-sso-client';
import config from '../Config';
import ContentStore from '../stores/ContentStore';
import LanguageStore from '../stores/LanguageStore';
import MenuStore from '../stores/MenuStore';
import RouteStore from '../stores/RouteStore';
import ShoppingCartStore from '../stores/ShoppingCartStore';
import SsoStore from '../stores/SsoStore';
import { TranslationsContext } from '@teliaee/telia-translate';
import { langFromUrl, pathnameWithoutLang } from '../util/NavigationUtil';

export interface PublicHeaderProps {
  isNode?: boolean;
  languageStore?: LanguageStore;
  routeStore?: RouteStore;
  ssoStore?: SsoStore;
  menuStore?: MenuStore;
  contentStore?: ContentStore;
  shoppingCartStore?: ShoppingCartStore;
  isMyTeliaUserAgent?: boolean;
}

@observer
class PublicHeader extends React.Component<PublicHeaderProps> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;

  private get lang() {
    return this.props.languageStore.currentLanguage;
  }

  private get isSsr() {
    return this.props.isNode;
  }

  private get currentPath() {
    return this.props.routeStore.pathname;
  }

  private get ssoClient(): SsoClient {
    return this.props.ssoStore.ssoClient;
  }

  private get customerType() {
    return this.props.ssoStore.customer && this.props.ssoStore.customer.type;
  }

  private get ssoClientState() {
    return this.props.ssoStore.ssoClientState;
  }

  private get loginOptions() {
    const { ssoFrameVisible, loginOptions } = this.props.ssoStore;
    return ssoFrameVisible ? loginOptions : undefined;
  }

  private get isCurrentContentAvailableInEnglish() {
    return (
      langFromUrl(this.currentPath) === 'en' ||
      this.currentPath.includes('/404') ||
      this.props.contentStore.isCurrentContentAvailableInEnglish ||
      this.props.menuStore.enMenuPaths.includes(pathnameWithoutLang(this.currentPath))
    );
  }

  private get customSegment(): string {
    if (this.props.contentStore.isPageThatShouldUseCookieSegmentForHeader) {
      return this.props.menuStore.getCustomMenuSegment();
    }
    return undefined;
  }

  private get tier() {
    return config.componentTier || 'live';
  }

  private get hideHeader() {
    return this.props.isMyTeliaUserAgent || this.props.contentStore.currentPageContent.hideHeader;
  }

  private get menu() {
    return this.props.menuStore.initialMenu;
  }

  private get cartItemsNumber() {
    return this.props.shoppingCartStore.cartItemsNumber;
  }

  private handleNavigation = (event: React.MouseEvent<HTMLElement>) => {
    this.props.routeStore.navigate(event);
  };

  private handleLangChange = (lang: 'et' | 'ru' | 'en') => {
    this.props.routeStore.setLanguage(lang);
  };

  private handleSectionChange = (section: string) => {
    const { closeSsoFrame, ssoFrameVisible } = this.props.ssoStore;

    if (ssoFrameVisible && section !== 'sso') {
      closeSsoFrame();
    }
  };

  public render(): JSX.Element {
    if (this.hideHeader) {
      return null;
    }

    return (
      <AppHeader
        app="public"
        tier={this.tier}
        lang={this.lang}
        isSPA={true}
        loginOptions={this.loginOptions}
        currentPath={this.currentPath}
        showEnLang={this.isCurrentContentAvailableInEnglish}
        customSegment={this.customSegment}
        translations={this.context.translations}
        ssoClient={this.ssoClient}
        ssoClientState={this.ssoClientState}
        onSectionChange={this.handleSectionChange}
        onLangChange={this.handleLangChange}
        onPathChange={this.handleNavigation}
        menu={this.menu}
        customerType={this.customerType}
        shoppingCartItems={this.cartItemsNumber}
        disableSoftAuth
        isSSR={this.isSsr}
      />
    );
  }
}

export default inject('isNode', 'languageStore', 'routeStore', 'ssoStore', 'menuStore', 'contentStore', 'shoppingCartStore')(PublicHeader);
